// frontend/src/components/SavedScenariosPanel.js

import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  Checkbox,
  ListItemIcon,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { Link, useNavigate } from 'react-router-dom';
import ConfirmationDialog from './ConfirmationDialog';

const SavedScenariosPanel = ({
  open,
  onClose,
  scenarios,
  onLoadScenario,
  onDeleteScenario
}) => {
  const [selectedForComparison, setSelectedForComparison] = useState([]);
  const navigate = useNavigate();
  
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [scenarioToDelete, setScenarioToDelete] = useState(null);

  const handleToggleCompare = (scenarioId) => {
    const currentIndex = selectedForComparison.indexOf(scenarioId);
    const newSelected = [...selectedForComparison];

    if (currentIndex === -1) {
      newSelected.push(scenarioId);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    if (newSelected.length > 2) {
        return;
    }
    setSelectedForComparison(newSelected);
  };

  const handleCompareClick = () => {
    if (selectedForComparison.length === 2) {
      navigate(`/report/compare/${selectedForComparison[0]}/${selectedForComparison[1]}`);
      onClose(); 
      setSelectedForComparison([]); 
    }
  };

  const handleDeleteClick = (scenarioId) => {
    setScenarioToDelete(scenarioId);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (scenarioToDelete) {
      onDeleteScenario(scenarioToDelete);
    }
    setConfirmOpen(false);
    setScenarioToDelete(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isOwner = (scenario) => {
    return true;
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: '400px' } }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FolderOpenIcon />
            <Typography variant="h6">Saved Scenarios</Typography>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {!scenarios || scenarios.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <Typography variant="body1" color="text.secondary">No saved scenarios yet</Typography>
            </Box>
          ) : (
            <List>
              {scenarios.map((scenario) => {
                const isSelected = selectedForComparison.includes(scenario.id);
                const isSelectable = scenario.status === 'completed' && (selectedForComparison.length < 2 || isSelected);

                return (
                <ListItem
                  key={scenario.id}
                  disablePadding
                  secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(scenario.id)}
                        disabled={!isOwner(scenario)}
                        sx={{ mr: 1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                  }
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: isSelected ? 'action.selected' : 'transparent',
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                >
                    <ListItemIcon sx={{pl: 1}}>
                      <Checkbox
                        edge="start"
                        checked={isSelected}
                        onChange={() => handleToggleCompare(scenario.id)}
                        disabled={!isSelectable}
                        inputProps={{ 'aria-labelledby': `scenario-label-${scenario.id}` }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      id={`scenario-label-${scenario.id}`}
                      primary={<Typography variant="subtitle1" component="div">{scenario.name}</Typography>}
                      // --- THIS IS THE FIX ---
                      // This tells the underlying Typography component to render as a <div> instead of a <p>
                      secondaryTypographyProps={{ component: 'div' }}
                      secondary={
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                              Hazard: {scenario.hazard_label}
                            </Typography>
                            <Chip
                              label={scenario.status}
                              color={scenario.status === 'completed' ? 'success' : (scenario.status === 'in_progress' ? 'info' : 'warning')}
                              size="small"
                              sx={{ textTransform: 'capitalize' }}
                            />
                        </Stack>
                      }
                    />
                    <Stack direction="row" spacing={1} sx={{ mr: 8, my: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FolderOpenIcon />}
                        onClick={() => onLoadScenario(scenario)}
                      >
                        Load
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AssessmentIcon />}
                        component={Link}
                        to={`/report/scenarios/${scenario.id}`}
                        disabled={scenario.status !== 'completed'}
                      >
                        Report
                      </Button>
                    </Stack>
                </ListItem>
              )})}
            </List>
          )}
        </DialogContent>

        <DialogActions sx={{ p: '16px 24px' }}>
          <Button
              onClick={handleCompareClick}
              disabled={selectedForComparison.length !== 2}
              variant="contained"
              color="secondary"
              startIcon={<CompareArrowsIcon />}
          >
              Compare Selected (2)
          </Button>
          <Button onClick={() => {
              onClose();
              setSelectedForComparison([]); 
          }}>
              Close
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Confirm Deletion"
        message="Are you sure you want to delete this scenario? This action cannot be undone."
      />
    </>
  );
};

export default SavedScenariosPanel;